import { useMemo, useState } from 'react';
import { useOpsStore } from '../../store/ops.store';
import type {
  InternalControlDepartmentSummary,
  InternalControlViolation,
} from '../../types/ops';
import { Shield, Download, Building2, ExternalLink, Search, ArrowUp, ArrowDown, ArrowUpDown, Users } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const CMS_BOOKING_URL = 'https://admin.25fit.com/bookings/details';

function formatDateTime(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy HH:mm');
  } catch {
    return dateStr;
  }
}

type SortKey =
  | 'bookingId'
  | 'memberName'
  | 'phoneNumber'
  | 'department'
  | 'position'
  | 'employeeStudioName'
  | 'studioName'
  | 'programName'
  | 'startTime';

type SortDir = 'asc' | 'desc';

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
  return dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
}

type EmpSortKey = 'memberName' | 'violationCount' | 'department' | 'position' | 'employeeStudioName';

interface EmployeeGroup {
  memberId: string;
  memberName: string;
  phoneNumber: string;
  department: string;
  position: string;
  employeeStudioName: string;
  violationCount: number;
  bookingIds: string[];
}

export default function InternalControlTable({ isDark }: { isDark: boolean }) {
  const internalControl = useOpsStore((s) => s.internalControl);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('startTime');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [empQuery, setEmpQuery] = useState('');
  const [empSortKey, setEmpSortKey] = useState<EmpSortKey>('violationCount');
  const [empSortDir, setEmpSortDir] = useState<SortDir>('desc');

  if (!internalControl) {
    return null;
  }

  const { violations, departmentSummary, summary } = internalControl;

  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? violations.filter((r) =>
          [
            r.bookingId,
            r.memberName,
            r.phoneNumber,
            r.department,
            r.position,
            r.employeeStudioName,
            r.studioName,
            r.programName,
          ]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q)),
        )
      : violations.slice();

    const cmp = (a: InternalControlViolation, b: InternalControlViolation): number => {
      const av = (a[sortKey] ?? '') as string;
      const bv = (b[sortKey] ?? '') as string;
      if (sortKey === 'startTime') {
        return new Date(av).getTime() - new Date(bv).getTime();
      }
      return String(av).localeCompare(String(bv), 'vi', { sensitivity: 'base' });
    };
    filtered.sort((a, b) => (sortDir === 'asc' ? cmp(a, b) : -cmp(a, b)));
    return filtered;
  }, [violations, query, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'startTime' ? 'desc' : 'asc');
    }
  };

  const employeeGroups = useMemo<EmployeeGroup[]>(() => {
    const map = new Map<string, EmployeeGroup>();
    for (const v of violations) {
      if (!map.has(v.memberId)) {
        map.set(v.memberId, {
          memberId: v.memberId,
          memberName: v.memberName,
          phoneNumber: v.phoneNumber,
          department: v.department,
          position: v.position,
          employeeStudioName: v.employeeStudioName,
          violationCount: 0,
          bookingIds: [],
        });
      }
      const g = map.get(v.memberId)!;
      g.violationCount++;
      g.bookingIds.push(v.bookingId);
    }
    return Array.from(map.values());
  }, [violations]);

  const filteredSortedEmployees = useMemo(() => {
    const q = empQuery.trim().toLowerCase();
    const filtered = q
      ? employeeGroups.filter((g) =>
          [g.memberName, g.phoneNumber, g.department, g.position, g.employeeStudioName]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q)),
        )
      : employeeGroups.slice();

    const cmp = (a: EmployeeGroup, b: EmployeeGroup): number => {
      if (empSortKey === 'violationCount') return a.violationCount - b.violationCount;
      const av = (a[empSortKey] ?? '') as string;
      const bv = (b[empSortKey] ?? '') as string;
      return String(av).localeCompare(String(bv), 'vi', { sensitivity: 'base' });
    };
    filtered.sort((a, b) => (empSortDir === 'asc' ? cmp(a, b) : -cmp(a, b)));
    return filtered;
  }, [employeeGroups, empQuery, empSortKey, empSortDir]);

  const toggleEmpSort = (key: EmpSortKey) => {
    if (empSortKey === key) {
      setEmpSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setEmpSortKey(key);
      setEmpSortDir(key === 'violationCount' ? 'desc' : 'asc');
    }
  };

  const exportEmployeesCsv = () => {
    const header = ['Tên nhân viên', 'SĐT', 'Phòng ban', 'Chức vụ', 'Studio NV', 'Số booking vi phạm', 'Booking IDs', 'Links CMS'];
    const csvRows = [header.join(',')];
    for (const g of filteredSortedEmployees) {
      csvRows.push([
        `"${(g.memberName || '').replace(/"/g, '""')}"`,
        `"${g.phoneNumber || ''}"`,
        `"${(g.department || '').replace(/"/g, '""')}"`,
        `"${(g.position || '').replace(/"/g, '""')}"`,
        `"${(g.employeeStudioName || '').replace(/"/g, '""')}"`,
        g.violationCount,
        `"${g.bookingIds.join(' | ')}"`,
        `"${g.bookingIds.map((id) => `${CMS_BOOKING_URL}/${id.toLowerCase()}`).join(' | ')}"`,
      ].join(','));
    }
    const blob = new Blob(['﻿' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'internal_control_by_employee.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    const header = ['Booking ID', 'Link CMS', 'Họ tên', 'SĐT', 'Phòng ban', 'Chức vụ', 'Studio NV', 'Booking Studio', 'Chương trình', 'Ngày', 'Giờ'];
    const csvRows = [header.join(',')];
    for (const row of filteredSorted) {
      csvRows.push([
        `"${row.bookingId}"`,
        `"${CMS_BOOKING_URL}/${row.bookingId.toLowerCase()}"`,
        `"${(row.memberName || '').replace(/"/g, '""')}"`,
        `"${row.phoneNumber || ''}"`,
        `"${(row.department || '').replace(/"/g, '""')}"`,
        `"${(row.position || '').replace(/"/g, '""')}"`,
        `"${(row.employeeStudioName || '').replace(/"/g, '""')}"`,
        `"${row.studioName.replace(/"/g, '""')}"`,
        `"${row.programName.replace(/"/g, '""')}"`,
        `"${row.bookingDate}"`,
        `"${formatDateTime(row.startTime)}"`,
      ].join(','));
    }
    const blob = new Blob(['﻿' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'internal_control_violations.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (summary.totalViolations === 0) {
    return (
      <div className={`rounded-lg p-6 text-center ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-white text-gray-500 border border-gray-200 shadow-sm'}`}>
        Không có vi phạm nào trong giai đoạn này. Nhân viên công ty không có booking Out of Time.
      </div>
    );
  }

  const headerBtnClass = `inline-flex items-center gap-1 hover:text-cyan-400 transition-colors ${isDark ? 'text-slate-300' : 'text-gray-600'}`;

  const SortableTh = ({
    keyName,
    label,
    sticky = false,
  }: {
    keyName: SortKey;
    label: string;
    sticky?: boolean;
  }) => (
    <th
      className={`px-3 py-3 font-medium whitespace-nowrap ${
        sticky ? `sticky left-0 z-20 ${isDark ? 'bg-slate-800' : 'bg-white'}` : ''
      }`}
    >
      <button type="button" onClick={() => toggleSort(keyName)} className={headerBtnClass}>
        {label}
        <SortIcon active={sortKey === keyName} dir={sortDir} />
      </button>
    </th>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className={`rounded-lg p-3 sm:p-4 ${isDark ? 'bg-slate-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <Building2 className="h-5 w-5 text-cyan-400" />
          <h3 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Thống kê theo phòng ban
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className={`border-b ${isDark ? 'border-slate-700 text-slate-300' : 'border-gray-200 text-gray-600'}`}>
                <th className="px-3 py-3 font-medium whitespace-nowrap">Phòng ban</th>
                <th className="px-3 py-3 font-medium whitespace-nowrap text-right">Số lần vi phạm</th>
                <th className="px-3 py-3 font-medium whitespace-nowrap text-right">Số nhân viên vi phạm</th>
                <th className="px-3 py-3 font-medium whitespace-nowrap text-right">Vi phạm / Nhân viên</th>
              </tr>
            </thead>
            <tbody>
              {departmentSummary.map((row: InternalControlDepartmentSummary, idx: number) => (
                <tr
                  key={row.department}
                  className={`border-b ${isDark ? 'border-slate-700/50' : 'border-gray-100'} ${
                    idx % 2 === 0
                      ? isDark ? 'bg-slate-800' : 'bg-white'
                      : isDark ? 'bg-slate-900' : 'bg-gray-50'
                  } ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-100'} transition-colors`}
                >
                  <td className={`px-3 py-2.5 whitespace-nowrap font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {row.department}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap text-right font-semibold ${isDark ? 'text-red-300' : 'text-red-600'}`}>
                    {row.violationCount.toLocaleString('en-US')}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap text-right ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    {row.memberCount.toLocaleString('en-US')}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap text-right ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    {(row.violationCount / Math.max(1, row.memberCount)).toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={`rounded-lg p-3 sm:p-4 ${isDark ? 'bg-slate-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <div className="flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
          <Users className="h-5 w-5 text-amber-400" />
          <h3 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Thống kê theo nhân viên ({filteredSortedEmployees.length}{filteredSortedEmployees.length !== employeeGroups.length ? ` / ${employeeGroups.length}` : ''})
          </h3>
          <div className="flex-1" />
          <div className="relative">
            <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
            <input
              type="text"
              value={empQuery}
              onChange={(e) => setEmpQuery(e.target.value)}
              placeholder="Tìm tên, SĐT, phòng ban, studio…"
              className={`pl-8 pr-3 py-1.5 rounded-lg text-xs border focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-colors w-64 ${
                isDark
                  ? 'bg-white/[0.03] border-white/10 text-white placeholder:text-slate-500 focus:border-cyan-500/50'
                  : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-cyan-500'
              }`}
            />
          </div>
          <button
            onClick={exportEmployeesCsv}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              isDark
                ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
        </div>
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="w-full text-sm text-left">
            <thead className={`sticky top-0 z-10 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
              <tr className={`border-b ${isDark ? 'border-slate-700 text-slate-300' : 'border-gray-200 text-gray-600'}`}>
                <th className={`px-3 py-3 font-medium whitespace-nowrap sticky left-0 z-20 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
                  <button type="button" onClick={() => toggleEmpSort('memberName')} className={headerBtnClass}>
                    Tên nhân viên
                    <SortIcon active={empSortKey === 'memberName'} dir={empSortDir} />
                  </button>
                </th>
                <th className="px-3 py-3 font-medium whitespace-nowrap text-right">
                  <button type="button" onClick={() => toggleEmpSort('violationCount')} className={headerBtnClass}>
                    Số booking vi phạm
                    <SortIcon active={empSortKey === 'violationCount'} dir={empSortDir} />
                  </button>
                </th>
                <th className="px-3 py-3 font-medium whitespace-nowrap">
                  <button type="button" onClick={() => toggleEmpSort('department')} className={headerBtnClass}>
                    Phòng ban
                    <SortIcon active={empSortKey === 'department'} dir={empSortDir} />
                  </button>
                </th>
                <th className="px-3 py-3 font-medium whitespace-nowrap">
                  <button type="button" onClick={() => toggleEmpSort('position')} className={headerBtnClass}>
                    Chức vụ
                    <SortIcon active={empSortKey === 'position'} dir={empSortDir} />
                  </button>
                </th>
                <th className="px-3 py-3 font-medium whitespace-nowrap">
                  <button type="button" onClick={() => toggleEmpSort('employeeStudioName')} className={headerBtnClass}>
                    Studio NV
                    <SortIcon active={empSortKey === 'employeeStudioName'} dir={empSortDir} />
                  </button>
                </th>
                <th className="px-3 py-3 font-medium whitespace-nowrap">Danh sách booking vi phạm</th>
              </tr>
            </thead>
            <tbody>
              {filteredSortedEmployees.map((g, idx) => (
                <tr
                  key={g.memberId}
                  className={`border-b ${isDark ? 'border-slate-700/50' : 'border-gray-100'} ${
                    idx % 2 === 0
                      ? isDark ? 'bg-slate-800' : 'bg-white'
                      : isDark ? 'bg-slate-900' : 'bg-gray-50'
                  } ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-100'} transition-colors`}
                >
                  <td className={`px-3 py-2.5 whitespace-nowrap sticky left-0 z-10 font-medium ${isDark ? 'text-white' : 'text-gray-900'} ${
                    idx % 2 === 0
                      ? isDark ? 'bg-slate-800' : 'bg-white'
                      : isDark ? 'bg-slate-900' : 'bg-gray-50'
                  }`}>
                    {g.memberName}
                    {g.phoneNumber && (
                      <span className={`ml-2 text-xs font-normal ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                        {g.phoneNumber}
                      </span>
                    )}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap text-right font-semibold ${isDark ? 'text-red-300' : 'text-red-600'}`}>
                    {g.violationCount.toLocaleString('en-US')}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>
                    {g.department}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    {g.position || '-'}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    {g.employeeStudioName || '-'}
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    <div className="flex flex-wrap gap-1 max-w-lg">
                      {g.bookingIds.map((id) => (
                        <a
                          key={id}
                          href={`${CMS_BOOKING_URL}/${id.toLowerCase()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={id}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-xs border transition-colors ${
                            isDark
                              ? 'border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 hover:border-cyan-500/50'
                              : 'border-cyan-200 text-cyan-700 hover:bg-cyan-50 hover:border-cyan-300'
                          }`}
                        >
                          {id.slice(0, 8).toLowerCase()}
                          <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={`rounded-lg p-3 sm:p-4 ${isDark ? 'bg-slate-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <div className="flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
          <Shield className="h-5 w-5 text-amber-400" />
          <h3 className={`text-base sm:text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Chi tiết vi phạm theo booking id ({filteredSorted.length}{filteredSorted.length !== violations.length ? ` / ${violations.length}` : ''})
          </h3>
          <div className="flex-1" />
          <div className="relative">
            <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Tìm tên, SĐT, phòng ban, studio…"
              className={`pl-8 pr-3 py-1.5 rounded-lg text-xs border focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-colors w-64 ${
                isDark
                  ? 'bg-white/[0.03] border-white/10 text-white placeholder:text-slate-500 focus:border-cyan-500/50'
                  : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-cyan-500'
              }`}
            />
          </div>
          <button
            onClick={exportCsv}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              isDark
                ? 'bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
        </div>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm text-left">
            <thead className={`sticky top-0 z-10 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
              <tr className={`border-b ${isDark ? 'border-slate-700 text-slate-300' : 'border-gray-200 text-gray-600'}`}>
                <SortableTh keyName="bookingId" label="Booking ID" sticky />
                <SortableTh keyName="memberName" label="Họ tên" />
                <SortableTh keyName="phoneNumber" label="SĐT" />
                <SortableTh keyName="department" label="Phòng ban" />
                <SortableTh keyName="position" label="Chức vụ" />
                <SortableTh keyName="employeeStudioName" label="Studio NV" />
                <SortableTh keyName="studioName" label="Booking Studio" />
                <SortableTh keyName="programName" label="Chương trình" />
                <SortableTh keyName="startTime" label="Ngày giờ" />
              </tr>
            </thead>
            <tbody>
              {filteredSorted.map((row: InternalControlViolation, idx: number) => (
                <tr
                  key={row.bookingId}
                  className={`border-b ${isDark ? 'border-slate-700/50' : 'border-gray-100'} ${
                    idx % 2 === 0
                      ? isDark ? 'bg-slate-800' : 'bg-white'
                      : isDark ? 'bg-slate-900' : 'bg-gray-50'
                  } ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-100'} transition-colors`}
                >
                  <td className={`px-3 py-2.5 whitespace-nowrap sticky left-0 z-10 ${
                    idx % 2 === 0
                      ? isDark ? 'bg-slate-800' : 'bg-white'
                      : isDark ? 'bg-slate-900' : 'bg-gray-50'
                  }`}>
                    <a
                      href={`${CMS_BOOKING_URL}/${row.bookingId.toLowerCase()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={row.bookingId}
                      className={`inline-flex items-center gap-1 font-mono text-xs hover:underline ${isDark ? 'text-cyan-300 hover:text-cyan-200' : 'text-cyan-700 hover:text-cyan-800'}`}
                    >
                      {row.bookingId.slice(0, 8).toLowerCase()}…
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {row.memberName}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    {row.phoneNumber}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>
                    {row.department}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    {row.position || '-'}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    {row.employeeStudioName || '-'}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    {row.studioName}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    {row.programName}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                    {formatDateTime(row.startTime)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
